import { useState } from "preact/hooks";
import { useNavigate } from "../contexts/AppContext";
import { PublicLayout } from "./PublicLayout";

// Two-step single-route flow:
//   step "email" — collect email, POST /api/auth/forgot-password.
//                  Always advances to step "reset" regardless of server
//                  response: the endpoint never reveals whether the
//                  account exists, and we mirror that in the UI by
//                  always showing the code-entry screen.
//   step "reset" — collect code + new password, POST /api/auth/reset-password.
//                  On success the server sets the session cookie and we
//                  navigate the user into the app.
//
// Refresh on /forgot drops back to step "email" — same trade-off as
// /verify: the in-memory email is gone, easier to retype than to
// reconstruct.

export function ForgotPassword({
  onAuthed,
}: {
  onAuthed: (email: string) => void;
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "reset">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [resendStatus, setResendStatus] = useState<"" | "sending" | "sent">("");

  const requestCode = async (e: Event) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      // Forgot-password is intentionally non-revealing: success regardless
      // of whether the email exists. Always advance.
      setStep("reset");
    } catch (err) {
      setError(String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitReset = async (e: Event) => {
    e.preventDefault();
    if (code.trim().length !== 6 || password.length < 8) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          code: code.trim(),
          password,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Reset failed");
        return;
      }
      const data = await res.json();
      onAuthed(data.user.email);
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next") || "/";
      navigate(next);
    } catch (err) {
      setError(String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setResendStatus("sending");
    try {
      await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      setResendStatus("sent");
    } catch {
      setResendStatus("");
    }
  };

  return (
    <PublicLayout>
      <div class="container py-5">
        <div class="row justify-content-center">
          <div class="col-md-6 col-lg-5">
            <div class="card shadow-sm">
              <div class="card-body p-4">
                {step === "email" ? (
                  <>
                    <h1 class="h3 mb-3 text-center">Reset your password</h1>
                    <p class="small text-muted text-center mb-4">
                      Enter your email and we'll send you a 6-digit code.
                    </p>
                    <form onSubmit={requestCode}>
                      <div class="mb-3">
                        <label class="form-label">Email</label>
                        <input
                          type="email"
                          class="form-control"
                          value={email}
                          onInput={(e) =>
                            setEmail((e.target as HTMLInputElement).value)
                          }
                          autoFocus
                          required
                        />
                      </div>
                      {error && (
                        <div class="alert alert-danger py-2">{error}</div>
                      )}
                      <button
                        type="submit"
                        class="btn btn-primary w-100"
                        disabled={submitting || !email.trim()}
                      >
                        <i class="bi bi-envelope"></i>{" "}
                        {submitting ? "..." : "Send code"}
                      </button>
                    </form>
                    <div class="text-center mt-3 small">
                      <a
                        href="/login"
                        onClick={(e: Event) => {
                          e.preventDefault();
                          navigate("/login");
                        }}
                      >
                        Back to log in
                      </a>
                    </div>
                  </>
                ) : (
                  <>
                    <h1 class="h3 mb-3 text-center">Choose a new password</h1>
                    <p class="small text-muted text-center mb-4">
                      If <strong>{email}</strong> matches a verified account, we
                      sent a 6-digit code. Enter it below along with your new
                      password.
                    </p>
                    <form onSubmit={submitReset}>
                      <div class="mb-3">
                        <label class="form-label">Code</label>
                        <input
                          type="text"
                          class="form-control form-control-lg text-center font-monospace"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          pattern="\d{6}"
                          value={code}
                          onInput={(e) =>
                            setCode(
                              (e.target as HTMLInputElement).value.replace(
                                /\D/g,
                                "",
                              ),
                            )
                          }
                          autoFocus
                          required
                        />
                      </div>
                      <div class="mb-3">
                        <label class="form-label">New password</label>
                        <input
                          type="password"
                          class="form-control"
                          value={password}
                          onInput={(e) =>
                            setPassword((e.target as HTMLInputElement).value)
                          }
                          minLength={8}
                          required
                        />
                        <div class="form-text">At least 8 characters.</div>
                      </div>
                      {error && (
                        <div class="alert alert-danger py-2">{error}</div>
                      )}
                      <button
                        type="submit"
                        class="btn btn-primary w-100"
                        disabled={
                          submitting || code.length !== 6 || password.length < 8
                        }
                      >
                        <i class="bi bi-check-lg"></i>{" "}
                        {submitting ? "..." : "Reset password"}
                      </button>
                    </form>
                    <div class="text-center mt-3 small">
                      {resendStatus === "sent" ? (
                        <span class="text-muted">
                          New code sent. Check your inbox.
                        </span>
                      ) : (
                        <>
                          Didn't get the email?{" "}
                          <button
                            type="button"
                            class="btn btn-link btn-sm p-0 align-baseline"
                            onClick={resend}
                            disabled={resendStatus === "sending"}
                          >
                            Resend
                          </button>
                        </>
                      )}
                    </div>
                    <div class="text-center mt-2 small">
                      <a
                        href="/forgot"
                        onClick={(e: Event) => {
                          e.preventDefault();
                          setStep("email");
                          setError("");
                          setCode("");
                          setPassword("");
                        }}
                      >
                        Use a different email
                      </a>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
